import React, { useRef } from 'react';
import './AssessmentReport.css';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const AssessmentReport = () => {
  const reportRef = useRef(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = React.useState(false);

  const generatePDF = async () => {
    if (!reportRef.current) return;
    
    setIsGeneratingPDF(true);
    
    try {
      // Temporarily hide the download button during capture
      const pdfBtn = document.querySelector('.pdf-download-btn');
      if (pdfBtn) pdfBtn.style.display = 'none';
      
      // Capture the report with high quality
      const canvas = await html2canvas(reportRef.current, {
        scale: 3, // Higher quality for print
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: reportRef.current.scrollWidth,
        height: reportRef.current.scrollHeight,
        windowWidth: reportRef.current.scrollWidth,
        windowHeight: reportRef.current.scrollHeight
      });

      // Restore the button
      if (pdfBtn) pdfBtn.style.display = 'flex';

      // Calculate dimensions for A4 (in mm)
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Create PDF in portrait mode
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Add the captured image to PDF
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      
      // Save the PDF
      const fileName = `Assessment_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      // Show success message
      alert('PDF downloaded successfully!');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="assessment-report-container">
      {/* PDF Download Button */}
      <div className="pdf-controls">
        <button 
          className="pdf-download-btn"
          onClick={generatePDF}
          disabled={isGeneratingPDF}
        >
          {isGeneratingPDF ? (
            <>
              <span className="pdf-spinner"></span>
              Generating PDF...
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 16L12 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M9 13L12 16L15 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18 16V18C18 19.1046 17.1046 20 16 20H8C6.89543 20 6 19.1046 6 18V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <rect x="4" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Download PDF Report
            </>
          )}
        </button>
        
        <div className="pdf-info">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="#2C3292" strokeWidth="1.5"/>
            <path d="M8 5V9M8 11V12" stroke="#2C3292" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span>PDF will maintain exact design and layout</span>
        </div>
      </div>

      {/* The Report */}
      <div className="assessment-report" ref={reportRef}>
        {/* Abstract Vector Waves with vibrant colors */}
        <div className="corner-overlays">
          <div className="corner-wave top-left">
            <svg width="350" height="350" viewBox="0 0 350 350" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0,0 C120,40 180,150 220,200 C260,250 300,180 350,120 L350,0 Z" fill="url(#grad1)" fillOpacity="0.3"/>
              <defs>
                <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#2C3292"/>
                  <stop offset="50%" stopColor="#0E4B95"/>
                  <stop offset="100%" stopColor="#006837"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          
          <div className="corner-wave top-right">
            <svg width="380" height="380" viewBox="0 0 380 380" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M380,0 C280,80 220,170 180,220 C140,270 80,300 0,320 L0,0 Z" fill="url(#grad2)" fillOpacity="0.35"/>
              <defs>
                <linearGradient id="grad2" x1="100%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#FFF200"/>
                  <stop offset="50%" stopColor="#39B54A"/>
                  <stop offset="100%" stopColor="#ED1C24"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          
          <div className="corner-wave bottom-left">
            <svg width="380" height="380" viewBox="0 0 380 380" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0,380 C100,280 180,200 240,150 C300,100 320,50 380,0 L380,380 Z" fill="url(#grad3)" fillOpacity="0.35"/>
              <defs>
                <linearGradient id="grad3" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#39B54A"/>
                  <stop offset="50%" stopColor="#0E4B95"/>
                  <stop offset="100%" stopColor="#2C3292"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          
          <div className="corner-wave bottom-right">
            <svg width="350" height="350" viewBox="0 0 350 350" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M350,350 C250,280 200,180 150,120 C100,60 60,30 0,0 L350,0 L350,350 Z" fill="url(#grad4)" fillOpacity="0.3"/>
              <defs>
                <linearGradient id="grad4" x1="100%" y1="100%" x2="0%" y2="0%">
                  <stop offset="0%" stopColor="#ED1C24"/>
                  <stop offset="50%" stopColor="#FFF200"/>
                  <stop offset="100%" stopColor="#39B54A"/>
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* Additional decorative elements */}
          <div className="floating-element element-1"></div>
          <div className="floating-element element-2"></div>
          <div className="floating-element element-3"></div>
          <div className="floating-element element-4"></div>
        </div>

        {/* Header Section with new color scheme */}
        <div className="header-section">
          <div className="main-header" style={{marginTop:'-1vh'}}>
            <h1 className="institution-name">DAR-E-ARQAM</h1>
            <div className="header-divider"></div>
          </div>
          <div className="campus-header">
            <h2>KOTLI LOHARAN EAST CAMPUS</h2>
          </div>
          <div className="project-banner">
            <div className="banner-content">
              <span className="banner-icon">🏛️</span>
              <h2 style={{color:'white'}}>Project of Punjab Group of Colleges</h2>
            </div>
          </div>
        </div>

        {/* Logo Section */}
        <div className="logo-section">
          <div className="logo-container royal-blue">
            <div className="logo-inner">
              <img src="/images/logo.jpg" alt="Logo 1" className="logo" />
            </div>
          </div>
          <div className="logo-container kelly-green">
            <div className="assessment-title">
              <div className="title-content">
                <div className="title-background">
                  <h1 className="assessment-heading">
                    <span className="superscript">2<sup>nd</sup></span> ASSESSMENT RESULT
                  </h1>
                </div>
                <div className="session-container">
                  <h2 className="session">Session 2024-2025</h2>
                  <div className="session-underline"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="logo-container navy-blue">
            <div className="logo-inner">
              <img src="/images/l2.png" alt="Logo 3" className="logo" />
            </div>
          </div>
        </div>

        {/* Student Info */}
        <div className="compact-info" style={{marginTop:'0vh',padding:'.5rem 0 0 .3rem'}}>
          <div className="info-container">
            <div className="info-header">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" stroke="#2C3292" strokeWidth="1.5"/>
                <path d="M10,5 L10,15 M5,10 L15,10" stroke="#39B54A" strokeWidth="1.5"/>
              </svg>
              <span className="info-title">Student Information</span>
            </div>
            <table className="student-info">
              <tbody>
                <tr>
                  <td><span className="info-label">Student Name:</span> Ali Haider Shamsi Ali Haider Shamsi Ali Haider Shamsi</td>
                  <td><span className="info-label">Class:</span> One</td>
                </tr>
                <tr>
                  <td><span className="info-label">Section:</span> Boys</td>
                  <td><span className="info-label">Campus:</span> Islam Campus</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

       {/* Subjects Table */}
<div className="compact-table-container" style={{marginTop:'0'}}>
  <div className="table-header">
    <div className="table-header-icon">📚</div>
    <h3 className="table-title" style={{color:'white'}}>Subjects Performance</h3>
    <div className="table-subtitle" style={{color:"#ffa600"}}>Detailed Marks Distribution</div>
  </div>
  <div className="table-content" style={{marginTop:'-1vh'}}>
    <table className="compact-table subjects-table">
      <colgroup>
        <col className="col-subject" />
        <col className="col-prev-total" />
        <col className="col-prev-obtained" />
        <col className="col-curr-total" />
        <col className="col-curr-obtained" />
        <col className="col-percentage" />
        <col className="col-grade" />
      </colgroup>
      <thead>
        <tr>
          <th rowSpan="2">Subjects</th>
          <th colSpan="2">Previous ASSESSMENTS</th>
          <th colSpan="2">CURRENT ASSESMENT</th>
          <th rowSpan="2">Percentage</th>
          <th rowSpan="2">Grade</th>
        </tr>
        <tr>
          <th>Total</th>
          <th>Obtained</th>
          <th>Total</th>
          <th>Obtained</th>
        </tr>
      </thead>
      <tbody>
        {[
          { 
            subject: 'English', 
            prevTotal: 75, prevObtained: 54,
            currTotal: 25, currObtained: 18, 
            percentage: 72, grade: 'B+' 
          },
          { 
            subject: 'Urdu', 
            prevTotal: 75, prevObtained: 57,
            currTotal: 25, currObtained: 19, 
            percentage: 76, grade: 'B+' 
          },
          { 
            subject: 'Math', 
            prevTotal: 75, prevObtained: 60,
            currTotal: 25, currObtained: 20, 
            percentage: 80, grade: 'A' 
          },
          { 
            subject: 'Science', 
            prevTotal: 75, prevObtained: 63,
            currTotal: 25, currObtained: 21, 
            percentage: 84, grade: 'A' 
          },
          { 
            subject: 'Computer', 
            prevTotal: 75, prevObtained: 66,
            currTotal: 25, currObtained: 22, 
            percentage: 88, grade: 'A+' 
          },
          { 
            subject: 'SST', 
            prevTotal: 75, prevObtained: 69,
            currTotal: 25, currObtained: 23, 
            percentage: 92, grade: 'A++' 
          }
        ].map((subject, index) => (
          <tr key={index}>
            <td className="subject-name">
              <span className="subject-icon">📘</span>
              {subject.subject}
            </td>
            <td className="prev-total-cell">{subject.prevTotal}</td>
            <td className="prev-obtained-cell">{subject.prevObtained}</td>
            <td className="curr-total-cell">{subject.currTotal}</td>
            <td className="curr-obtained-cell">{subject.currObtained}</td>
            <td className={`percentage-cell percentage-${Math.floor(subject.percentage / 10)}`}>
              <div className="percentage-wrapper">
                <span className="percentage-badge">{subject.percentage}%</span>
                <div className="percentage-bar" style={{ width: `${subject.percentage}%` }}></div>
              </div>
            </td>
            <td className="grade-cell">
              <span className={`grade-badge grade-${subject.grade.replace('+', 'plus').replace('++', 'plusplus')}`}>
                {subject.grade}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>

        {/* Summary Table */}
        <div
  className="compact-table-container summary-table-container"
  style={{ marginTop: '0' }}
>
  <div
    className="table-header"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '6px'
    }}
  >
    <div className="table-header-icon">🏆</div>
    <h3
      className="table-title"
      style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}
    >
      Overall Performance
    </h3>
  </div>

  <table
    className="compact-table summary-table"
    style={{
      width: '100%',
      borderCollapse: 'collapse',
      tableLayout: 'fixed'
    }}
  >
    <thead>
      <tr>
        {['Grand Total', 'Obtained', 'Percentage', 'Grade', 'Remarks'].map(
          (title, i) => (
            <th
              key={i}
              style={{
                textAlign: 'center',
                verticalAlign: 'middle',
                padding: '10px 8px',
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
            >
              {title}
            </th>
          )
        )}
      </tr>
    </thead>

    <tbody>
      <tr>
        <td
          className="highlight total-highlight"
          style={{
            textAlign: 'center',
            verticalAlign: 'middle',
            padding: '10px 8px'
          }}
        >
          230
        </td>

        <td
          className="highlight obtained-highlight"
          style={{
            textAlign: 'center',
            verticalAlign: 'middle',
            padding: '10px 8px'
          }}
        >
          214.5
        </td>

        <td
          className="highlight"
          style={{
            textAlign: 'center',
            verticalAlign: 'middle',
            padding: '10px 8px'
          }}
        >
          <div
            className="percentage-wrapper"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <span className="percentage-badge highlight-percentage">93%</span>
            <div
              className="percentage-bar highlight-bar"
              style={{
                width: '93%',
                height: '6px',
                borderRadius: '4px'
              }}
            ></div>
          </div>
        </td>

        <td
          className="highlight"
          style={{
            textAlign: 'center',
            verticalAlign: 'middle',
            padding: '10px 8px'
          }}
        >
          <span className="grade-badge grade-A+ highlight-grade">A+</span>
        </td>

        <td
          className="highlight remarks-highlight"
          style={{
            textAlign: 'center',
            verticalAlign: 'middle',
            padding: '10px 8px',
            whiteSpace: 'nowrap'
          }}
        >
          Exceptional
        </td>
      </tr>
    </tbody>
  </table>
</div>


        {/* Grading Scheme */}
        <div className="grading-section" style={{marginTop:"-1vh"}}>
          <div className="section-header">
            <div className="header-line left-line"></div>
            <div className="section-title">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="section-icon">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#2C3292"/>
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="#FFF200" strokeWidth="1.5"/>
              </svg>
              <h2 className="grading-title">Grading Scheme</h2>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="section-icon">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#ED1C24"/>
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="#39B54A" strokeWidth="1.5"/>
              </svg>
            </div>
            <div className="header-line right-line"></div>
          </div>
          <div className="grading-content">
            <div className="grading-table-wrapper">
              <div className="grading-header">
                <h4 className="grading-subtitle">Grade Evaluation Criteria</h4>
                <div className="grading-info">Based on Percentage Score</div>
              </div>
              <table className="grading-table">
                <colgroup>
                  <col className="col-grade-label" />
                  <col className="col-percentage-range" />
                  <col className="col-grade-remarks" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Grade</th>
                    <th>Percentage</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { grade: 'A+', percentage: '90% or Above', remarks: 'Exceptional', color: '#39B54A', bgColor: 'rgba(57, 181, 74, 0.1)' },
                    { grade: 'A', percentage: '80 to 89%', remarks: 'Excellent', color: '#0E4B95', bgColor: 'rgba(14, 75, 149, 0.1)' },
                    { grade: 'B+', percentage: '70 to 79%', remarks: 'Very Good', color: '#006837', bgColor: 'rgba(0, 104, 55, 0.1)' },
                    { grade: 'B', percentage: '60 to 69%', remarks: 'Good', color: '#2C3292', bgColor: 'rgba(44, 50, 146, 0.1)' },
                    { grade: 'C', percentage: '50 to 59%', remarks: 'Fair', color: '#FFF200', bgColor: 'rgba(255, 242, 0, 0.1)' },
                    { grade: 'D', percentage: '40 to 49%', remarks: 'Need Improvement', color: '#FF9800', bgColor: 'rgba(255, 152, 0, 0.1)' },
                    { grade: 'E', percentage: 'Below 40%', remarks: 'Reappear', color: '#ED1C24', bgColor: 'rgba(237, 28, 36, 0.1)' }
                  ].map((item, index) => (
                    <tr key={index} style={{ backgroundColor: item.bgColor }}>
                      <td className="grade-label-cell">
                        <span className="compact-grade-badge" style={{ 
                          backgroundColor: item.color,
                          boxShadow: `0 2px 8px ${item.color}40`
                        }}>
                          {item.grade}
                        </span>
                      </td>
                      <td className="percentage-range-cell">
                        <div className="range-display">
                          <span className="range-text">{item.percentage}</span>
                        </div>
                      </td>
                      <td className="grade-remarks-cell">
                        <div className="remark-display">
                          <span className="remark-text">{item.remarks}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="footer-content">
              <div className="note-box">
                <div className="note-header">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="note-icon">
                    <circle cx="10" cy="10" r="9" fill="#0E4B95" fillOpacity="0.1" stroke="#0E4B95" strokeWidth="1.5"/>
                    <path d="M10 5V11M10 14V15" stroke="#2C3292" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <h4 className="note-title">Important Note</h4>
                </div>
                <p className="note">
                  <b>Attention:</b> This report is system-generated. If you notice any discrepancies, 
                  please visit the school office within 3 working days. Regards!
                </p>
              </div>
              
              <div className="signatures-section">
                <div className="signatures-header">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="#39B54A" fillOpacity="0.2"/>
                    <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="#39B54A" strokeWidth="1.5"/>
                    <path d="M20 22V20C20 17.7909 18.2091 16 16 16H8C5.79086 16 4 17.7909 4 20V22" stroke="#2C3292" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span className="signatures-title">Authorized Signatures</span>
                </div>
                <div className="signatures">
                  {[
                    { title: 'Principal', name: 'Sir Sheraz', color: '#2C3292' },
                    { title: 'Class Teacher', name: 'Sir XYZ', color: '#39B54A' },
                    { title: 'Coordinator', name: 'Mam Amina Rafiq', color: '#0E4B95' }
                  ].map((signatory, index) => (
                    <div key={index} className="signature-block">
                      <div className="signature-line" style={{ background: `linear-gradient(to right, transparent, ${signatory.color}, transparent)` }}></div>
                      <div className="signature-info">
                        <p className="signature-title" style={{ color: signatory.color }}>{signatory.title}</p>
                        <p className="signature-name">{signatory.name}</p>
                      </div>
                      <div className="signature-stamp" style={{ backgroundColor: signatory.color + '20' }}>
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                          <circle cx="20" cy="20" r="18" stroke={signatory.color} strokeWidth="1.5"/>
                          <path d="M12 12L28 28M28 12L12 28" stroke={signatory.color} strokeWidth="1.5"/>
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer decorative elements */}
        <div className="footer-waves">
          <svg width="100%" height="40" viewBox="0 0 1200 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,20 C200,5 400,35 600,20 C800,5 1000,35 1200,20 L1200,40 L0,40 Z" fill="url(#footerGradient)" fillOpacity="0.8"/>
            <defs>
              <linearGradient id="footerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#2C3292"/>
                <stop offset="33%" stopColor="#39B54A"/>
                <stop offset="66%" stopColor="#FFF200"/>
                <stop offset="100%" stopColor="#ED1C24"/>
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* Quality Seal */}
        <div className="quality-seal">
          <div className="seal-inner">
            <div className="seal-circle">
              <span className="seal-text">CERTIFIED</span>
              <span className="seal-subtext">Academic Record</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentReport;